import { Controller } from "@hotwired/stimulus"
import { showPaymentProcessingOverlay } from "../payment_processing_overlay"

export default class extends Controller {
  static targets = [
    "splitToggle", "splitSection", "singleCardSection", "card1Section", "card2Section",
    "splitTypeSelect", "splitTypePills", "customAmountSection",
    "card1Amount", "card2Amount", "card1AmountDisplay", "card2AmountDisplay",
    "card1BadgeAmount", "card2BadgeAmount",
    "card1Installments", "card2Installments",
    "totalAmount", "submitButton",
    "progressIndicator", "card1Status", "card2Status",
    "card1AmountInput", "card2AmountInput",
    "card1InstallmentsInput", "card2InstallmentsInput",
    "card1Tab", "card2Tab",
    "splitBar1", "splitBar2",
    "card1Summary", "card2Summary",
    "backButton", "inlineCaptchaSection", "inlineSubmitButton"
  ]

  static values = {
    totalCents: Number,
    maxInstallments: Number,
    paymentToken: String,
    singlePaymentUrl: String,
    splitPaymentUrl: String
  }

  connect() {
    this.splitEnabled = false
    this.activeCard = 1
    this.card1Complete = false
    this.card2Complete = false
    this.autoAdvanceEnabled = true // Enable auto-advance by default
    this.autoAdvanceDebounceTimer = null
    this.installmentRates = this.getInstallmentRates()
    // Find the form - it's an ancestor, not a descendant, so we need to find it manually
    this.form = this.element.closest('form')
    this.updateDisplay()

    // Setup auto-advance listeners after a short delay to ensure DOM is ready
    setTimeout(() => this.setupAutoAdvanceListeners(), 100)
  }

  setupAutoAdvanceListeners() {
    // Listen to input events on Card 1 fields for auto-advance
    if (this.hasCard1SectionTarget) {
      const card1Fields = this.card1SectionTarget.querySelectorAll('input')
      card1Fields.forEach(field => {
        field.addEventListener('input', () => this.checkCard1AutoAdvance())
      })
    }

    // Listen to input events on Card 2 fields to mark completion
    if (this.hasCard2SectionTarget) {
      const card2Fields = this.card2SectionTarget.querySelectorAll('input')
      card2Fields.forEach(field => {
        field.addEventListener('input', () => this.checkCard2Completion())
      })
    }
  }

  // Check if Card 1 is complete and auto-advance to Card 2
  checkCard1AutoAdvance() {
    if (!this.splitEnabled || !this.autoAdvanceEnabled || this.card1Complete || this.activeCard !== 1) {
      return
    }

    // Debounce to avoid rapid transitions
    clearTimeout(this.autoAdvanceDebounceTimer)
    this.autoAdvanceDebounceTimer = setTimeout(() => {
      const validation = this.validateCard1Fields()
      if (validation.valid) {
        this.autoAdvanceToCard2()
      }
    }, 500) // 500ms delay after last keystroke
  }

  autoAdvanceToCard2() {
    // Mark Card 1 as complete and smoothly transition
    this.card1Complete = true
    this.showCard2()
  }

  // Check Card 2 completion for visual indicator
  checkCard2Completion() {
    if (!this.splitEnabled || this.activeCard !== 2) {
      return
    }

    const validation = this.validateCard2Fields()
    if (validation.valid && !this.card2Complete) {
      this.card2Complete = true
      this.updateTabStyles()
      this.updateActionButtons()
    } else if (!validation.valid && this.card2Complete) {
      this.card2Complete = false
      this.updateTabStyles()
      this.updateActionButtons()
    }
  }

  validateCard2Fields() {
    const errors = []
    const section = this.card2SectionTarget

    // Get card 2 fields
    const cardNumber = section.querySelector('input[name="card2_profile[card_number]"]')?.value?.replace(/\s/g, '') || ''
    const holderName = section.querySelector('input[name="card2_profile[holder_name]"]')?.value?.trim() || ''
    const expiration = section.querySelector('input[name="card2_profile[card_expiration]"]')?.value?.trim() || ''
    const cvv = section.querySelector('input[name="card2_profile[card_cvv]"]')?.value?.trim() || ''

    // Same validation as Card 1
    if (cardNumber.length < 13 || cardNumber.length > 19) {
      errors.push({ field: 'card_number', message: 'Número do cartão inválido' })
    }
    if (holderName.length < 3) {
      errors.push({ field: 'holder_name', message: 'Nome no cartão é obrigatório' })
    }
    if (!/^\d{2}\/\d{2}$/.test(expiration)) {
      errors.push({ field: 'card_expiration', message: 'Validade inválida' })
    }
    if (!/^\d{3,4}$/.test(cvv)) {
      errors.push({ field: 'card_cvv', message: 'CVV inválido' })
    }

    return { valid: errors.length === 0, errors }
  }

  getInstallmentRates() {
    // Approximate monthly rates based on e-Rede rates
    // These are used to calculate approximate installment values
    return {
      1: 0,
      2: 0.0269,
      3: 0.0269,
      4: 0.0269,
      5: 0.0269,
      6: 0.0269,
      7: 0.0289,
      8: 0.0289,
      9: 0.0289,
      10: 0.0289,
      11: 0.0289,
      12: 0.0289
    }
  }

  toggleSplitPayment(event) {
    this.splitEnabled = event.target.checked

    // Reset completion flags and auto-advance timer
    this.card1Complete = false
    this.card2Complete = false
    clearTimeout(this.autoAdvanceDebounceTimer)

    if (this.splitEnabled) {
      this.splitSectionTarget.classList.remove("hidden")
      this.singleCardSectionTarget.classList.add("hidden")
      this.updateSplitAmounts()

      // Mark form as split payment mode (CSS uses this to hide original captcha section)
      if (this.form) {
        this.form.dataset.splitPaymentEnabled = 'true'
      }

      // Show card 1 by default (instant, no animation)
      this.showCard1Instant()

      // Scroll to card section when enabling split
      this.scrollToCardSection()
    } else {
      this.splitSectionTarget.classList.add("hidden")
      this.singleCardSectionTarget.classList.remove("hidden")

      // Remove split payment mode marker (shows original captcha section again)
      if (this.form) {
        delete this.form.dataset.splitPaymentEnabled
      }

      // Hide inline captcha section
      if (this.hasInlineCaptchaSectionTarget) {
        this.inlineCaptchaSectionTarget.classList.add('hidden')
      }

      // Scroll to card section when disabling split
      this.scrollToCardSection()
    }

    this.updateFormAction()
  }

  scrollToCardSection() {
    setTimeout(() => {
      const parentCard = this.element.closest('.bg-white.rounded-2xl')
      if (parentCard) {
        const yOffset = -20
        const y = parentCard.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }, 100)
  }

  updateFormAction() {
    if (this.form) {
      if (this.splitEnabled) {
        // Use configured URL or fallback to direct sale URL for backwards compatibility
        const splitUrl = this.hasSplitPaymentUrlValue
          ? this.splitPaymentUrlValue
          : `/venda-direta/pagamento/${this.paymentTokenValue}/processar-parcelado`
        this.form.action = splitUrl
        // Add hidden field to indicate split payment
        this.addHiddenField('is_split_payment', 'true')
      } else {
        // Use configured URL or fallback to direct sale URL for backwards compatibility
        const singleUrl = this.hasSinglePaymentUrlValue
          ? this.singlePaymentUrlValue
          : `/venda-direta/pagamento/${this.paymentTokenValue}/processar`
        this.form.action = singleUrl
        this.removeHiddenField('is_split_payment')
      }
    }
  }

  addHiddenField(name, value) {
    let field = this.form.querySelector(`input[name="${name}"]`)
    if (!field) {
      field = document.createElement('input')
      field.type = 'hidden'
      field.name = name
      this.form.appendChild(field)
    }
    field.value = value
  }

  removeHiddenField(name) {
    const field = this.form.querySelector(`input[name="${name}"]`)
    if (field) {
      field.remove()
    }
  }

  selectSplitType(event) {
    const splitType = event.target.value

    if (splitType === "custom") {
      this.customAmountSectionTarget.classList.remove("hidden")
    } else {
      this.customAmountSectionTarget.classList.add("hidden")
      this.applySplitType(splitType)
    }

    this.updateDisplay()
  }

  selectSplitTypePill(event) {
    const button = event.currentTarget
    const splitType = button.dataset.splitType

    // Update hidden input
    if (this.hasSplitTypeSelectTarget) {
      this.splitTypeSelectTarget.value = splitType
    }

    // Update pill styles
    const pills = this.splitTypePillsTarget.querySelectorAll('.split-pill')
    pills.forEach(pill => {
      pill.classList.remove('border-green-500', 'bg-green-50', 'text-green-700')
      pill.classList.add('border-gray-200', 'bg-white', 'text-gray-600')
    })
    button.classList.remove('border-gray-200', 'bg-white', 'text-gray-600')
    button.classList.add('border-green-500', 'bg-green-50', 'text-green-700')

    // Handle custom amount section
    if (splitType === "custom") {
      this.customAmountSectionTarget.classList.remove("hidden")
      // Pre-fill with current amounts
      const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
      const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0
      if (this.hasCard1AmountTarget) {
        this.card1AmountTarget.value = this.formatMoney(card1Amount)
      }
      if (this.hasCard2AmountTarget) {
        this.card2AmountTarget.value = this.formatMoney(card2Amount)
      }
    } else {
      this.customAmountSectionTarget.classList.add("hidden")
      this.applySplitType(splitType)
    }

    this.updateDisplay()
    this.updateSplitBar()
  }

  // Instant show (no animation) for initial state
  showCard1Instant() {
    this.activeCard = 1
    this.card1SectionTarget.classList.remove("hidden")
    this.card2SectionTarget.classList.add("hidden")
    this.updateTabStyles()
  }

  showCard1() {
    if (this.activeCard === 1) return
    this.activeCard = 1
    this.animateTransition(this.card2SectionTarget, this.card1SectionTarget, 'right')
    this.updateTabStyles()
  }

  showCard2() {
    if (this.activeCard === 2) return
    this.activeCard = 2
    this.animateTransition(this.card1SectionTarget, this.card2SectionTarget, 'left')
    this.updateTabStyles()
  }

  // Simple crossfade transition - no layout issues
  animateTransition(fromSection, toSection, direction) {
    const duration = 200 // ms

    // Fade out current section
    fromSection.style.transition = `opacity ${duration}ms ease-out`
    fromSection.style.opacity = '0'

    // After fade out, switch sections and fade in
    setTimeout(() => {
      fromSection.classList.add('hidden')
      fromSection.style.transition = ''
      fromSection.style.opacity = ''

      toSection.classList.remove('hidden')
      toSection.style.opacity = '0'
      toSection.style.transition = `opacity ${duration}ms ease-in`

      // Small delay to ensure the transition applies
      requestAnimationFrame(() => {
        toSection.style.opacity = '1'
      })

      // Cleanup after animation
      setTimeout(() => {
        toSection.style.transition = ''
        toSection.style.opacity = ''
      }, duration + 50)
    }, duration)
  }

  // Validate Card 1 and show Card 2 if valid
  validateAndShowCard2() {
    const validation = this.validateCard1Fields()

    if (!validation.valid) {
      this.showValidationErrors(validation.errors)
      return
    }

    // Mark Card 1 as complete and switch to Card 2
    this.card1Complete = true
    this.updateTabStyles()
    this.showCard2()
  }

  validateCard1Fields() {
    const errors = []
    const section = this.card1SectionTarget

    // Get card 1 fields
    const cardNumber = section.querySelector('input[name="card1_profile[card_number]"]')?.value?.replace(/\s/g, '') || ''
    const holderName = section.querySelector('input[name="card1_profile[holder_name]"]')?.value?.trim() || ''
    const expiration = section.querySelector('input[name="card1_profile[card_expiration]"]')?.value?.trim() || ''
    const cvv = section.querySelector('input[name="card1_profile[card_cvv]"]')?.value?.trim() || ''

    // Validate card number (13-19 digits)
    if (cardNumber.length < 13 || cardNumber.length > 19) {
      errors.push({ field: 'card_number', message: 'Número do cartão inválido' })
    }

    // Validate holder name (at least 3 characters)
    if (holderName.length < 3) {
      errors.push({ field: 'holder_name', message: 'Nome no cartão é obrigatório' })
    }

    // Validate expiration (MM/YY format)
    if (!/^\d{2}\/\d{2}$/.test(expiration)) {
      errors.push({ field: 'card_expiration', message: 'Validade inválida (MM/AA)' })
    } else {
      const [month, year] = expiration.split('/')
      const expMonth = parseInt(month, 10)
      const expYear = parseInt('20' + year, 10)
      const now = new Date()
      const expDate = new Date(expYear, expMonth, 0) // Last day of expiration month

      if (expMonth < 1 || expMonth > 12 || expDate < now) {
        errors.push({ field: 'card_expiration', message: 'Cartão expirado ou data inválida' })
      }
    }

    // Validate CVV (3-4 digits)
    if (!/^\d{3,4}$/.test(cvv)) {
      errors.push({ field: 'card_cvv', message: 'CVV inválido' })
    }

    return { valid: errors.length === 0, errors }
  }

  showValidationErrors(errors) {
    const section = this.card1SectionTarget

    // Clear previous error styles
    section.querySelectorAll('.border-red-300').forEach(el => {
      el.classList.remove('border-red-300')
    })
    section.querySelectorAll('.validation-error').forEach(el => el.remove())

    // Add error styles and messages
    errors.forEach(error => {
      const input = section.querySelector(`input[name="card1_profile[${error.field}]"]`)
      if (input) {
        input.classList.add('border-red-300')

        // Add error message below field
        const errorEl = document.createElement('p')
        errorEl.className = 'mt-1 text-sm text-red-600 validation-error'
        errorEl.textContent = error.message
        input.parentNode.appendChild(errorEl)
      }
    })

    // Focus first error field
    const firstError = section.querySelector('.border-red-300')
    if (firstError) {
      firstError.focus()
    }
  }

  showToast(message, type = 'info') {
    // Remove existing toasts
    document.querySelectorAll('.split-payment-toast').forEach(el => el.remove())

    const toast = document.createElement('div')
    toast.className = 'split-payment-toast fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 opacity-0 translate-y-[-20px]'

    if (type === 'success') {
      toast.classList.add('bg-green-600', 'text-white')
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      `
    } else if (type === 'error') {
      toast.classList.add('bg-red-600', 'text-white')
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      `
    } else {
      toast.classList.add('bg-green-600', 'text-white')
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      `
    }

    document.body.appendChild(toast)

    // Trigger animation
    setTimeout(() => {
      toast.classList.remove('opacity-0', 'translate-y-[-20px]')
      toast.classList.add('opacity-100', 'translate-y-0')
    }, 10)

    // Remove after 4 seconds
    setTimeout(() => {
      toast.classList.add('opacity-0', 'translate-y-[-20px]')
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  updateTabStyles() {
    if (!this.hasCard1TabTarget || !this.hasCard2TabTarget) return

    // Clear all styling classes first (segmented control style - neutral colors)
    const classes = [
      'bg-white', 'bg-green-50', 'shadow-sm',
      'text-gray-800', 'text-green-600', 'text-gray-500',
      'font-semibold', 'font-medium'
    ]
    classes.forEach(c => {
      this.card1TabTarget.classList.remove(c)
      this.card2TabTarget.classList.remove(c)
    })

    // Card 1 styling - Neutral segmented control
    if (this.activeCard === 1) {
      // Active: white background with shadow, dark text
      this.card1TabTarget.classList.add('bg-white', 'shadow-sm', 'text-gray-800', 'font-semibold')
    } else if (this.card1Complete) {
      // Completed but inactive: subtle green background (brand success color)
      this.card1TabTarget.classList.add('bg-green-50', 'text-green-600', 'font-medium')
    } else {
      // Incomplete inactive: no background, gray text
      this.card1TabTarget.classList.add('text-gray-500', 'font-medium')
    }

    // Card 2 styling - Neutral segmented control
    if (this.activeCard === 2) {
      // Active: white background with shadow, dark text
      this.card2TabTarget.classList.add('bg-white', 'shadow-sm', 'text-gray-800', 'font-semibold')
    } else if (this.card2Complete) {
      // Completed but inactive: subtle green background (brand success color)
      this.card2TabTarget.classList.add('bg-green-50', 'text-green-600', 'font-medium')
    } else {
      // Incomplete inactive: no background, gray text
      this.card2TabTarget.classList.add('text-gray-500', 'font-medium')
    }
  }

  updateSplitBar() {
    if (!this.hasSplitBar1Target || !this.hasSplitBar2Target) return

    const total = this.totalCentsValue
    const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
    const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0

    const card1Percentage = (card1Amount / total) * 100
    const card2Percentage = (card2Amount / total) * 100

    this.splitBar1Target.style.width = `${card1Percentage}%`
    this.splitBar2Target.style.width = `${card2Percentage}%`
  }

  applySplitType(splitType) {
    const total = this.totalCentsValue
    let card1Amount, card2Amount

    switch (splitType) {
      case "50_50":
        card1Amount = Math.floor(total / 2)
        card2Amount = total - card1Amount
        break
      case "60_40":
        card1Amount = Math.floor(total * 0.6)
        card2Amount = total - card1Amount
        break
      case "70_30":
        card1Amount = Math.floor(total * 0.7)
        card2Amount = total - card1Amount
        break
      default:
        card1Amount = Math.floor(total / 2)
        card2Amount = total - card1Amount
    }

    this.card1AmountInputTarget.value = card1Amount
    this.card2AmountInputTarget.value = card2Amount

    this.updateDisplay()
    this.updateSplitBar()
  }

  updateSplitAmounts() {
    // Default to 50/50 split
    this.applySplitType("50_50")
  }

  updateCustomAmount(event) {
    const total = this.totalCentsValue
    const input = event.target

    // Check which input was changed by comparing with targets
    const isCard1 = this.hasCard1AmountTarget && input === this.card1AmountTarget

    if (isCard1) {
      // Cap card1 amount to total
      let card1Amount = this.parseMoneyInput(input.value)
      if (card1Amount > total) {
        card1Amount = total
        input.value = this.formatMoney(card1Amount)
      }
      const card2Amount = Math.max(0, total - card1Amount)

      this.card1AmountInputTarget.value = card1Amount
      this.card2AmountInputTarget.value = card2Amount

      // Update the other visible input field
      if (this.hasCard2AmountTarget) {
        this.card2AmountTarget.value = this.formatMoney(card2Amount)
      }
    } else {
      // Cap card2 amount to total
      let card2Amount = this.parseMoneyInput(input.value)
      if (card2Amount > total) {
        card2Amount = total
        input.value = this.formatMoney(card2Amount)
      }
      const card1Amount = Math.max(0, total - card2Amount)

      this.card1AmountInputTarget.value = card1Amount
      this.card2AmountInputTarget.value = card2Amount

      // Update the other visible input field
      if (this.hasCard1AmountTarget) {
        this.card1AmountTarget.value = this.formatMoney(card1Amount)
      }
    }

    this.updateDisplay()
    this.updateSplitBar()
    this.validateAmounts()
  }

  updateInstallments(event) {
    this.updateDisplay()
  }

  updateDisplay() {
    if (!this.splitEnabled) return

    const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
    const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0

    // Update amount displays
    if (this.hasCard1AmountDisplayTarget) {
      this.card1AmountDisplayTarget.textContent = this.formatMoney(card1Amount)
    }
    if (this.hasCard2AmountDisplayTarget) {
      this.card2AmountDisplayTarget.textContent = this.formatMoney(card2Amount)
    }

    // Update badge amounts
    if (this.hasCard1BadgeAmountTarget) {
      this.card1BadgeAmountTarget.textContent = this.formatMoney(card1Amount)
    }
    if (this.hasCard2BadgeAmountTarget) {
      this.card2BadgeAmountTarget.textContent = this.formatMoney(card2Amount)
    }

    // Update dropdown options with installment values
    this.updateInstallmentOptions(this.card1InstallmentsInputTarget, card1Amount)
    this.updateInstallmentOptions(this.card2InstallmentsInputTarget, card2Amount)

    // Update visual bar
    this.updateSplitBar()
  }

  updateInstallmentOptions(selectElement, amountCents) {
    if (!selectElement) return

    const currentValue = selectElement.value
    const maxInstallments = this.maxInstallmentsValue || 12

    // Clear and rebuild options
    selectElement.innerHTML = ''

    for (let i = 1; i <= maxInstallments; i++) {
      const installmentValue = this.calculateInstallmentValue(amountCents, i)
      const option = document.createElement('option')
      option.value = i

      if (i === 1) {
        option.textContent = `1x de ${this.formatMoney(amountCents)} (à vista)`
      } else {
        option.textContent = `${i}x de ${this.formatMoney(installmentValue)}`
      }

      selectElement.appendChild(option)
    }

    // Restore previous selection
    selectElement.value = currentValue
  }

  calculateInstallmentValue(amountCents, installments) {
    if (installments <= 1) return amountCents

    // For simplicity, we're not adding interest in the display
    // The actual installment value with interest is calculated server-side
    return Math.ceil(amountCents / installments)
  }

  parseMoneyInput(value) {
    // Parse "R$ 7.000,00" or "7000.00" format
    const cleanValue = value.replace(/[R$\s.]/g, "").replace(",", ".")
    return Math.round(parseFloat(cleanValue) * 100) || 0
  }

  formatMoney(cents) {
    const value = cents / 100
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    })
  }

  validateAmounts() {
    const total = this.totalCentsValue
    const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
    const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0

    const isValid = (card1Amount + card2Amount) === total &&
                    card1Amount > 0 &&
                    card2Amount > 0

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !isValid
    }

    return isValid
  }

  handleSplitSubmit(event) {
    if (!this.splitEnabled) return

    if (!this.validateAmounts()) {
      event.preventDefault()
      alert("A soma dos valores dos cartões deve ser igual ao total da compra")
      return
    }

    // Validate Card 1 fields
    const card1Validation = this.validateCard1Fields()
    if (!card1Validation.valid) {
      event.preventDefault()
      this.showCard1()
      this.showValidationErrors(card1Validation.errors)
      return
    }

    // Validate Card 2 fields
    const card2Validation = this.validateCard2Fields()
    if (!card2Validation.valid) {
      event.preventDefault()
      this.showCard2()
      this.showCard2ValidationErrors(card2Validation.errors)
      return
    }

    // Show processing overlay
    this.showProcessingOverlay()
  }

  showCard2ValidationErrors(errors) {
    const section = this.card2SectionTarget

    // Clear previous error styles
    section.querySelectorAll('.border-red-300').forEach(el => {
      el.classList.remove('border-red-300')
    })
    section.querySelectorAll('.validation-error').forEach(el => el.remove())

    // Add error styles and messages
    errors.forEach(error => {
      const input = section.querySelector(`input[name="card2_profile[${error.field}]"]`)
      if (input) {
        input.classList.add('border-red-300')

        // Add error message below field
        const errorEl = document.createElement('p')
        errorEl.className = 'mt-1 text-sm text-red-600 validation-error'
        errorEl.textContent = error.message
        input.parentNode.appendChild(errorEl)
      }
    })

    // Focus first error field
    const firstError = section.querySelector('.border-red-300')
    if (firstError) {
      firstError.focus()
    }
  }

  showProcessingOverlay() {
    showPaymentProcessingOverlay()
  }

  // Update action buttons based on completion status
  updateActionButtons() {
    if (!this.hasBackButtonTarget) return

    if (this.card1Complete && this.card2Complete) {
      // Both complete - hide Back button, show inline captcha + Finalizar section
      this.backButtonTarget.classList.add('hidden')

      if (this.hasInlineCaptchaSectionTarget) {
        this.inlineCaptchaSectionTarget.classList.remove('hidden')

        // Scroll to show the inline section
        setTimeout(() => {
          this.inlineCaptchaSectionTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 200)
      }
    } else {
      // Not complete - show Back button, hide inline captcha
      this.backButtonTarget.classList.remove('hidden')

      if (this.hasInlineCaptchaSectionTarget) {
        this.inlineCaptchaSectionTarget.classList.add('hidden')
      }
    }
  }
}
